import {AxiosError} from 'axios';
import BASE_URL from '../../config/env';
import {
  BackendErrorData,
  parseApiError,
} from '../../utils/custom-errors/parseApiError';
import {apiClient} from '../apiClient';
import {DocumentListSourceInfo} from '../types/document.type';
import {
  AdditionMessageToBackgroundRequest,
  DocumentAuditReportRequestBody,
  DocumentAuditReportResponse,
  DocumentAuditSaveRequestPayload,
  DocumentAuditSaveResponse,
  ExportDataReportRequest,
  ExportDataRunReportRequest,
  ExportUserActivityReportRequestPayload,
  ExportUserActivityReportResponse,
  ExportDocumentAuditReportRequestPayload,
  ExportDocumentAuditReportResponse,
  GetABMDocJSONResultReqPayload,
  GetABMJSONResultReqPayload,
  GetABMViewDataReqPayload,
  GetABMViewDataResponse,
  GetDocumentMasterDocIdResponse,
  GetReviewReportRequest,
  GetReviewReportResponse,
  GetReviewResultsPayload,
  GetScrubCharPayload,
  GetScrubCharResponse,
  GetTimeLineResponse,
  GetTimelineToReportMasterDocIdResponse,
  GetTimelineTotallyDocMasterDocIdResponse,
  IDeleteReportResponse,
  IGetDocReportRequest,
  IGetDocReportResponse,
  IGetExportViewRequest,
  IGetExportViewResponse,
  IGetReportDocIdRequest,
  IGetReportDocIdResponse,
  IGetReportMasterDocListIdRequest,
  IGetReportMasterDocListIdResponse,
  IReportLandingResponse,
  ISavedReportListResponse,
  ISaveExportDataReportRequestBody,
  ISaveReportRequestBody,
  ISaveReportResponse,
  ISaveTimelineGapReportRequestBody,
  ISourceViewRequestBody,
  ISourceViewResponse,
  ISubtallyDataRequestBody,
  ISubtallyDataResponseBody,
  ISubtallyResultRequestBody,
  ISubtallyResultResponseBody,
  ITallyDataRequestBody,
  ITallyDataResponseBody,
  ITallyResultRequestBody,
  ITallyResultResponseBody,
  LeftPanelReportResponse,
  NavigationMasterListRequestPayload,
  NavigationMasterListResponse,
  RedirectReportRequestPayload,
  RedirectReportResponse,
  ReviewerCountReportRequest,
  ReviewerCountsExportRequest,
  ReviewerCountsExportResponse,
  ReviewerProductivityReportPayload,
  ReviewerProductivityViewPayload,
  ReviewerTimeOnlineRequest,
  ReviewerTimeViewRequest,
  ReviewerLeftPanelResponse,
  ReviewLeftPanelQuery,
  ReviewLoginExportExcelRequest,
  ReviewLoginExportExcelResponse,
  ReviewProgressAssignmentExportRequest,
  SaveABMReportDataReqPayload,
  SaveAssignGroupGridReportDataReqPayload,
  SaveReviewResultsReportDataPayload,
  SaveReviewResultsReportDataResponse,
  SearchTermAddMessageRequestPayload,
  SearchTermTotalSelectedCountRequestPayload,
  SearchTermTotalSelectedCountResponse,
  SubTallyExportRequestBody,
  SubTallyExportResponseBody,
  TagCountReportReqPayload,
  TagCountReportResponse,
  TagCountReportViewReqPayload,
  TagCountReportViewResponse,
  TallyExportRequestBody,
  TallyExportResponseBody,
  UserLoginActivityJsonReqPayload,
  UserLoginActivitySaveRequestPayload,
  UserLoginActivitySaveResponse,
  ReviewResultExportExcelRequest,
  ReviewResultExportExcelResponse,
  ReviewerProgressPayload,
  GetRPRGridDataResponse,
  ReviewProgressReviewerExportRequest,
  ReviewerCountViewReportResponse,
  ReviewerCountReportResponse,
  ReviewerCountViewReportRequest,
  ReviewerCountsExportReviewReportsRequest,
  UserActivityReportRequestBody,
  UserActivityReportResponse,
  UserActivitySaveRequestPayload,
  UserActivitySaveResponse,
} from '../types/reportingApi.type';

const reportingEndpoint = (path: string) =>
  `${BASE_URL}/reporting/api/v1/report${path}`;
const reportingReviewEndpoint = (path: string) =>
  `/reporting/api/v1/review${path}`;

const reportingDataAnalysisEndpoint = (path: string) =>
  `/reporting/api/v1/dataanalysisreport${path}`;

const saveReportEndpoint = (path: string) =>
  `/reporting/api/v1/sharereports${path}`;

const docManagementEndpoint = (path: string) =>
  `documentmanagement/api/v1/${path}`;

export const getReportLanding = async (
  payload = {}
): Promise<IReportLandingResponse> => {
  const {data} = await apiClient.post(reportingEndpoint('/landing'), payload);
  return data;
};

export const getUserActivityView = async (): Promise<any> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/user-activity/user-list')
  );
  return data;
};

export const getDocumentAuditActionTypes = async (): Promise<any> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/document-audit/action-type')
  );
  return data;
};

export const getDocumentAuditSearchList = async (): Promise<any> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/document-audit/search-list')
  );
  return data;
};

export const saveDocumentAuditReportData = async (
  payload: DocumentAuditSaveRequestPayload
): Promise<DocumentAuditSaveResponse> => {
  const response = await apiClient.post(
    reportingReviewEndpoint('/document-audit/save'),
    payload, // <-- second argument is the body
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return response?.data;
};

export const getDocumentAuditReportResultView = async (
  payload: DocumentAuditReportRequestBody
): Promise<DocumentAuditReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/document-audit/result'),
    payload,
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return data;
};

export const exportDocumentAuditReport = async (
  payload: ExportDocumentAuditReportRequestPayload
): Promise<ExportDocumentAuditReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/document/audit-export-excel'),
    payload,
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return data;
};

export const getUserActivityActionTypes = async (): Promise<any> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/document-audit/action-type')
  );
  return data;
};

export const saveUserActivityReportData = async (
  payload: UserActivitySaveRequestPayload
): Promise<UserActivitySaveResponse> => {
  const response = await apiClient.post(
    reportingReviewEndpoint('/save-user-activity'),
    payload, // <-- second argument is the body
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return response?.data;
};

// helper to wait
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getUserActivityReportResultView = async (
  payload: UserActivityReportRequestBody
): Promise<UserActivityReportResponse> => {
  // ⏳ wait 6 seconds before making the API call
  await wait(6000);

  const {data} = await apiClient.post(
    reportingReviewEndpoint('/user-activity-report'),
    payload,
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return data;
};

export const exportUserActivityReport = async (
  payload: ExportUserActivityReportRequestPayload
): Promise<ExportUserActivityReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/user-export-exl'),
    payload,
    {
      headers: {'Content-Type': 'application/json'},
    }
  );
  return data;
};

export const getSourceView = async (
  payload: ISourceViewRequestBody
): Promise<ISourceViewResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/tally/view'),
    payload
  );
  return data;
};

export const fetchTallyReport = async (
  payload: ITallyDataRequestBody
): Promise<ITallyDataResponseBody> => {
  const {data} = await apiClient.post(reportingEndpoint('/tally'), payload);
  return data;
};

export const fetchTallyResult = async (
  payload: ITallyResultRequestBody
): Promise<ITallyResultResponseBody> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/tally/view/result'),
    payload
  );
  return data;
};

export const fetchSubtallyReport = async (
  payload: ISubtallyDataRequestBody
): Promise<ISubtallyDataResponseBody> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/subtally/report'),
    payload
  );
  return data;
};

export const fetchSubtallyResult = async (
  payload: ISubtallyResultRequestBody
): Promise<ISubtallyResultResponseBody> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/subtally/view'),
    payload
  );
  return data;
};

export const getReviewResultsSources = async () => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/review-results/source-list')
  );
  return data;
};

export const getLeftPanelData = async (reportTypeCode: number) => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/left-panel'),
    {params: {reportTypeCode}}
  );
  return data;
};
export const SaveReviewResultsReportData = async (
  payload: SaveReviewResultsReportDataPayload
): Promise<SaveReviewResultsReportDataResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/review-results/save'),
    payload
  );
  return data;
};

export const getReviewResults = async (payload: GetReviewResultsPayload) => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint(
      '/review-results/view/result?reportId=' + payload.reportID
    )
  );
  return data;
};

export const getTallyMetadataFilters = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `metadatafilters/getmetadatafilter?SourceTypeCode=Tally`
    )
  );
  return data;
};

export const getSubTallyMetadataFilters = async () => {
  const {data} = await apiClient.get(
    docManagementEndpoint(
      `metadatafilters/getmetadatafilter?SourceTypeCode=SubTally`
    )
  );
  return data;
};

export const getUserList = async () => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint(`/user-activity/user-list`)
  );
  return data;
};

export const getUserListGridData = async (
  payload: UserLoginActivityJsonReqPayload
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/user-login-activity/json'),
    payload
  );
  return data;
};
export const getTimelineView = async (payload: {reportID: number}) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/timeline/view'),
    payload
  );
  return data;
};

export const getReportTimeline = async (
  payload: Record<string, any>
): Promise<GetTimeLineResponse> => {
  const {data} = await apiClient.post(reportingEndpoint('/timeline'), payload);
  return data;
};

export const getReportTimelineResult = async (
  payload: Record<string, any>
): Promise<Record<string, any>> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/timeline/view/result'),
    payload
  );
  return data;
};

export const saveUserLoginActivity = async (
  payload: UserLoginActivitySaveRequestPayload
): Promise<UserLoginActivitySaveResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/user-login-activity/save'),
    payload
  );
  return data;
};

export const getSavedReportList = async (
  reportTypeCode: string
): Promise<ISavedReportListResponse> => {
  const {data} = await apiClient.get(
    reportingEndpoint(
      `/reportlist/saved-report?reportTypeCode=${reportTypeCode}`
    )
  );
  return data;
};

//save report api
export const saveReport = async (
  payload: ISaveReportRequestBody
): Promise<ISaveReportResponse> => {
  try {
    const {data} = await apiClient.post(
      saveReportEndpoint('/report-xml'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiError(error as AxiosError<BackendErrorData>, 'Reporting');
  }
};

export const saveTimelineReportXml = async (
  payload: ISaveTimelineGapReportRequestBody
): Promise<ISaveReportResponse> => {
  try {
    const {data} = await apiClient.post(
      saveReportEndpoint('/savetimelinereportxml'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiError(error as AxiosError<BackendErrorData>, 'Reporting');
  }
};

export const getExportDataFormat = async (payload: ExportDataReportRequest) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/export/view'),
    payload
  );
  return data;
};

export const getScrubChar = async (
  payload: GetScrubCharPayload
): Promise<GetScrubCharResponse> => {
  const {data} = await apiClient.get(
    reportingEndpoint('/scru-character'),
    {params: payload}
  );
  return data;
};

export const runExportReport = async (payload: ExportDataRunReportRequest) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/export/add-message'),
    payload
  );
  return data;
};

export const saveExportDataReport = async (
  payload: ISaveExportDataReportRequestBody
): Promise<ISaveReportResponse> => {
  try {
    const {data} = await apiClient.post(
      saveReportEndpoint('/saveexportdatareportxml'),
      payload
    );
    return data;
  } catch (error) {
    throw parseApiError(error as AxiosError<BackendErrorData>, 'Reporting');
  }
};

export const getSearchListForSearchTermReport = async (payload: any[]) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint(`/search-term/search-list`),
    payload
  );
  return data;
};

export const getSearchTermAddMessage = async (
  payload: SearchTermAddMessageRequestPayload
): Promise<any> => {
  const {data} = await apiClient.post(
    reportingDataAnalysisEndpoint(`/term-add-message`),
    payload
  );
  return data;
};

export const getSearchTermResultJson = async (params: any): Promise<any> => {
  const {data} = await apiClient.get(
    reportingDataAnalysisEndpoint(
      `/search-term?ReportId=${params?.ReportId}&PageNumber=${params?.PageNumber}&PageSize=${params?.PageSize}&SortingColumn=${params?.SortingColumn}&SortingDirection=${params?.SortingDirection}`
    )
  );
  return data;
};

export const getTotalSelectedCounts = async (
  payload: SearchTermTotalSelectedCountRequestPayload
): Promise<SearchTermTotalSelectedCountResponse> => {
  const {data} = await apiClient.post(
    reportingDataAnalysisEndpoint(`/selected-count`),
    payload
  );
  return data;
};

export const saveABMReportFilterData = async (
  payload: SaveABMReportDataReqPayload
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/abm-report'),
    payload
  );
  return data;
};

export const getABMReportViewData = async (
  payload: GetABMViewDataReqPayload
): Promise<GetABMViewDataResponse> => {
  const {data} = await apiClient.get(reportingReviewEndpoint('/abm-view'), {
    params: payload,
  });
  return data;
};

export const getABMJSONResultData = async (
  payload: GetABMJSONResultReqPayload
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/abm-result'),
    payload
  );
  return data;
};

export const getABMDocJSONResultData = async (
  payload: GetABMDocJSONResultReqPayload
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/abm-doc-result'),
    payload
  );
  return data;
};

export const saveAssignGroupGridReportData = async (
  payload: SaveAssignGroupGridReportDataReqPayload
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/assign-group-grid'),
    payload
  );
  return data;
};

export const getReviewReport = async (
  payload: GetReviewReportRequest
): Promise<GetReviewReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/review-report'),
    payload
  );
  return data;
};

export const deleteReport = async (
  reportId: number
): Promise<IDeleteReportResponse> => {
  const {data} = await apiClient.delete(
    reportingEndpoint(`/delete-report?reportId=${reportId}`)
  );
  return data;
};

export const getLeftPanelReport = async (
  reportId: number
): Promise<LeftPanelReportResponse> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/leftpanel-report'),
    {params: {reportId}}
  );
  return data;
};

export const exportReviewerCounts = async (
  payload: ReviewerCountsExportRequest
): Promise<ReviewerCountsExportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-counts/export'),
    payload
  );
  return data;
};

export const exportReviewProgressAssignment = async (
  payload: ReviewProgressAssignmentExportRequest
): Promise<ReviewerCountsExportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-counts/export'),
    payload
  );
  return data;
};

export const getNotificationStatus = async (notificationID: number) => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint(
      `/notifications/status?notificationId=${notificationID}`
    )
  );
  return data;
};

export const getNotificationFlag = async (
  reportID: number,
  notificationID: number
) => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint(
      `/notification-flag?reportID=${reportID}&notificationID=${notificationID}`
    )
  );
  return data;
};

export const cancelBackgroundTask = async (
  reportID: number,
  notificationID: number
) => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint(
      `/cancle-bg-task?reportId=${reportID}&notificationId=${notificationID}`
    )
  );
  return data;
};

export const getTallyExportReport = async (
  payload: TallyExportRequestBody
): Promise<TallyExportResponseBody> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/tally-export-report'),
    payload
  );
  return data;
};

export const getSubTallyExportReport = async (
  payload: SubTallyExportRequestBody
): Promise<SubTallyExportResponseBody> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/subtally/export'),
    payload
  );
  return data;
};

export const getReportState = async (
  reportId: number | string
): Promise<any> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint(`/state-report?reportId=${reportId}`)
  );
  return data;
};

export const exportExcelGapReport = async (param: {
  queryParam: {gapReportType: string};
  payload: Record<string, any>;
}) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/export-excel-gapreport'),
    param.payload,
    {params: param.queryParam}
  );
  return data;
};

export const getDocumentMasterDocId = async (
  payload: object
): Promise<GetDocumentMasterDocIdResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/master-doc'),
    payload
  );
  return data;
};

export const getReportDocId = async (
  payload: IGetReportDocIdRequest
): Promise<IGetReportDocIdResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/background-report'),
    payload
  );
  return data;
};
export const getDocReport = async (
  payload: IGetDocReportRequest
): Promise<IGetDocReportResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/doc-report'),
    payload
  );
  return data;
};

export const getExportView = async (
  payload: IGetExportViewRequest
): Promise<IGetExportViewResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/export/view'),
    payload
  );
  return data;
};
export const getTimelineToReportMasterDocId = async (
  payload: object
): Promise<GetTimelineToReportMasterDocIdResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/timeline-timelinetoreport-doc'),
    payload
  );
  return data;
};

export const timelineBackground = async (payload: any) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/timeline-background'),
    payload
  );
  return data;
};

export const getReportDocListMasterID = async (
  payload: DocumentListSourceInfo
): Promise<number> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/doclist-master'),
    payload
  );
  return data;
};

export const getDocListMasterIDForExport = async (
  payload: DocumentListSourceInfo
): Promise<number> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/doclist-export'),
    payload
  );
  return data;
};

export const getTimelineTotallyDocMasterId = async (
  payload: object
): Promise<GetTimelineTotallyDocMasterDocIdResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/timeline-timelinetotally-doc'),
    payload
  );
  return data;
};

export const getReportDocumentMasterDocId = async (
  payload: IGetReportMasterDocListIdRequest
): Promise<IGetReportMasterDocListIdResponse> => {
  const {data} = await apiClient.post(
    reportingEndpoint('/master-doclist'),
    payload
  );
  return data;
};

export const additionMessageToBackground = async (
  payload: AdditionMessageToBackgroundRequest
): Promise<{status: number}> => {
  const response = await apiClient.post(
    reportingEndpoint('/background-message'),
    payload
  );
  return {status: response.status};
};

export const timelineDoc = async (payload: any) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/timeline-doc'),
    payload
  );
  return data;
};

export const redirectReport = async (
  payload: RedirectReportRequestPayload
): Promise<RedirectReportResponse> => {
  const {data} = await apiClient.post(
    reportingDataAnalysisEndpoint('/redirect-report'),
    payload
  );
  return data;
};

export const getNavigationMasterList = async (
  payload: NavigationMasterListRequestPayload
): Promise<NavigationMasterListResponse> => {
  const {data} = await apiClient.post(
    docManagementEndpoint(`document/navigation-master-list`),
    payload
  );
  return data;
};

export const batchReportExportToExcel = async (payload: any) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/batch-report-exporttoexcel'),
    payload
  );
  return data;
};

export const exportReviewLoginExcel = async (
  payload: ReviewLoginExportExcelRequest
): Promise<ReviewLoginExportExcelResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/login-export-exl'),
    payload,
    {headers: {'Content-Type': 'application/json'}}
  );
  return data;
};

export const exportReviewResultExcel = async (
  payload: ReviewResultExportExcelRequest
): Promise<ReviewResultExportExcelResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/review-export-exl'),
    payload,
    {headers: {'Content-Type': 'application/json'}}
  );
  return data;
};

export const exportTimlineGapReportToExcel = async (payload: any) => {
  const {data} = await apiClient.post(
    reportingEndpoint('/export-excle-timeline'),
    payload
  );
  return data;
};

export const getLeftPanel = async (
  reportTypeCode: number
): Promise<LeftPanelReportResponse> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/left-panel'),
    {params: {reportTypeCode}}
  );
  return data;
};

export const getReviewerTimeOnlineReport = async (
  payload: ReviewerTimeOnlineRequest
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-online-report'),
    payload
  );
  return data;
};

export const getReviewerProductivityReport = async (
  payload: ReviewerProductivityReportPayload
): Promise<Record<string, any>> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-productivity-report'),
    payload
  );
  return data;
};

export const getReviewerTimeOnlineView = async (
  payload: ReviewerTimeViewRequest
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-online-view'),
    payload
  );

  return data;
};

export const getReviewLeftPanel = async (
  query: ReviewLeftPanelQuery
): Promise<ReviewerLeftPanelResponse> => {
  const {data} = await apiClient.get(
    reportingReviewEndpoint('/left-panel'),
    {
      params: {
        reportTypeCode: query?.reportTypeCode ?? '',
      },
    }
  );

  return data;
};

export const getReviewerCountReport = async (
  payload: ReviewerCountReportRequest
): Promise<ReviewerCountReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/review-reports/reviewer-count'),
    payload
  );

  return data;
};

export const getReviewerCountReportView = async (
  payload: ReviewerCountViewReportRequest
): Promise<ReviewerCountViewReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/review-reports/reviewer-count-view'),
    payload
  );

  return data;
};

export const tagCountReport = async (
  payload: TagCountReportReqPayload
): Promise<TagCountReportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/tag-count-report'),
    payload
  );
  return data;
};

export const tagCountView = async (
  payload: TagCountReportViewReqPayload
): Promise<TagCountReportViewResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/tag-count-view'),
    payload
  );
  return data;
};

export const reviewerCountExport = async (payload: any) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-counts/export'),
    payload
  );
  return data;
};

export const getReviewerProductivityView = async (
  payload: ReviewerProductivityViewPayload
): Promise<Record<string, any>> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-productivity-view'),
    payload
  );
  return data;
};

export const saveReviewerProgressReportData = async (
  payload: ReviewerProgressPayload
) => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/save-reviewer-report'),
    payload
  );
  return data;
};

export const getRPRGridData = async (
  payload: GetReviewReportRequest
): Promise<GetRPRGridDataResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/grid-progress-summary'),
    payload
  );
  return data;
};

export const exportReviewProgressReviewer = async (
  payload: ReviewProgressReviewerExportRequest
): Promise<ReviewerCountsExportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-counts/export'),
    payload
  );
  return data;
};

export const exportReviewerCountsReport = async (
  payload: ReviewerCountsExportReviewReportsRequest
): Promise<ReviewerCountsExportResponse> => {
  const {data} = await apiClient.post(
    reportingReviewEndpoint('/reviewer-counts/export'),
    payload,
    {headers: {'Content-Type': 'application/json'}}
  );
  return data;
};
